import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useParams } from 'react-router-dom'
import LoadingSquare from '../../../components/common/LoadingSquare'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAlert } from 'react-alert'
import { setPath } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import { LangValue } from '../../../../states/types/common'
import MemberListModule from '../../../components/common/MemberListModule'
import SettingModal from '../../../components/common/SettingModal'
import { RequestPatchTaskDisableTaskCreationSetting, TaskDisableTaskCreationMember } from '../../../../states/types/task'
import { fetchTaskDisableTaskCreationSetting, updateTaskDisableTaskCreationSetting } from '../../../../states/containers/task/taskSlice'
import { IconName } from '@fortawesome/fontawesome-common-types'
import { RootState } from '../../../../states/containers/store'

const STATUS_LIST = [
  {
    label: "生成しない",
    value: true,
  },
  {
    label: "生成する",
    value: false,
  },
]

const SELECT_LIST = [
  {
    label: "会社全体の設定に準拠する",
    value: "common",
  },
  {
    label: "生成しない",
    value: "on",
  },
  {
    label: "生成する",
    value: "off",
  },
]

const Index: FC = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [isDisableTaskCreation, setIsDisableTaskCreation] = useState<boolean>(false)

  const [originMemberList, setOriginMemberList] = useState<TaskDisableTaskCreationMember[]>([])
  const [commonValue, setCommonValue] = useState<boolean>(true)

  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false)
  const [targetMembers, setTargetMembers] = useState<TaskDisableTaskCreationMember[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const openUpdateModal = (memberIds: number[]) => {
    setTargetMembers(originMemberList.filter((v) => memberIds.includes(v.id)))
    setIsDisplayModal(true)
  }

  const closeModal = () => {
    setTargetMembers([])
    setIsDisplayModal(false)
  }

  const buildParam = (type: string) => {
    switch (type) {
      case "on":
        return true
      case "off":
        return false
      default:
        return null
    }
  }

  const buildParamToString = (type: boolean | null) => {
    if (type === null) {
      return "common"
    }
    if (type) return "on"
    return "off"
  }

  const changeDisableTaskCreation = (data: {commonFlg: boolean, memberIds?: number[], type: string}) => {
    const param: RequestPatchTaskDisableTaskCreationSetting = { change_common_setting: data.commonFlg }
    const disable_task_creation = buildParam(data.type)
    if (data.commonFlg) {
      param.disable_task_creation = disable_task_creation ?? true
    } else {
      param.member_ids = data.memberIds
      param.use_common_setting = disable_task_creation === null
      if (data.type !== "common") {
        param.disable_task_creation = disable_task_creation ?? true
      }
    }
    dispatch(updateTaskDisableTaskCreationSetting(
      param,
      () => {
        closeModal()
        if (data.commonFlg) {
          setCommonValue(disable_task_creation ?? true)
          alert.success("会社全体の設定を変更しました")
        } else {
          setOriginMemberList((cur) => cur.map((v) => (
            param.member_ids?.includes(v.id)
              ? { ...v, use_common_setting: param.use_common_setting || false, disable_task_creation: param.disable_task_creation ?? null }
              : v
          )))
          alert.success("ユーザーの設定を変更しました")
        }
      },
    ))
  }

  useEffect(() => {
    setIsLoading(true)
    dispatch(fetchTaskDisableTaskCreationSetting(
      (result) => {
        setOriginMemberList(result.members)
        setCommonValue(result.common.disable_task_creation)
        setIsDisableTaskCreation(result.common.disable_task_creation)
        setIsLoading(false)
      },
    ))
  }, [])

  return (
    <section className="task_page setting_page">
      <Helmet>
        <title>
          {
            location.pathname === `/${language}${URL.SETTING_TASK_EXCLUDE_GENERATE_MEMBER_SETTING.path}` ? (
              `Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - 作業データの非自動生成設定`
            ) : (
              `Qasee - 工数管理 - 作業データの非自動生成設定`
            )
          }
        </title>
      </Helmet>
      <section className="breadcrumb">
        {
          location.pathname === `/${language}${URL.SETTING_TASK_EXCLUDE_GENERATE_MEMBER_SETTING.path}` ? (
            <>
              設定
              &nbsp;&gt;&nbsp;
              <Link
                to={setPath(URL.SETTING_COMMON.path, language)}
              >
                {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
              </Link>
            </>
          ) : (
            <>
              工数管理
            </>
          )
        }
        &nbsp;&gt;&nbsp;
        作業データの非自動生成設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          作業データの非自動生成設定
        </h1>
      </section>
      <section className="page_description">
        <p style={{ marginBottom: "4px" }}>作業データの自動生成を設定します。</p>
        <p style={{ marginBottom: "4px" }}>
          自動生成しない設定では、
          <span style={{ color: "#FF0000", fontWeight: "bold" }}>自動的に作業データが作成されません。</span>
          ご注意ください。
        </p>
      </section>
      {
        isLoading ? (
          <LoadingSquare>
            <>
              設定を読み込んでいます…
            </>
          </LoadingSquare>
        ) : (
          <>
            {
              login.role_target_restriction || (
                <section className="page_contents content-area">
                  <div className="content-block">
                    <div className="block-header" style={{ display: "block" }}>
                      <div className="title" style={{ marginBottom: "16px" }}>
                        会社全体の設定
                      </div>
                    </div>
                    <div className="block-detail">
                      <div
                        className={`graphical_check_box${isDisableTaskCreation ? " selected" : ""}`}
                        onClick={() => setIsDisableTaskCreation(!isDisableTaskCreation)}
                        role="none"
                      >
                        <FontAwesomeIcon
                          icon={['far', `${isDisableTaskCreation ? 'check-square' : 'square'}` as IconName]}
                          className={`icon ${isDisableTaskCreation ? "checked" : "no_check"}`}
                        />
                        <div>
                          <div className="check_box_title">
                            作業データの自動生成をしない
                          </div>
                          <div className="check_box_description">
                            作業データの自動生成を設定します
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="mt-4 button"
                        onClick={() => changeDisableTaskCreation({ commonFlg: true, type: isDisableTaskCreation ? "on" : "off" })}
                      >
                        設定する
                      </button>
                    </div>
                  </div>
                </section>
              )
            }
            <MemberListModule
              originMemberList={originMemberList.map((v) => ({ ...v, value: v.disable_task_creation }))}
              statusList={STATUS_LIST}
              commonValue={commonValue}
              valueLabel="作業データの非自動生成設定"
              type="bool"
              modalOpenCallback={(memberIds) => openUpdateModal(memberIds)}
            />
            <SettingModal
              targetMemberList={targetMembers.map((v) => ({ ...v, value: buildParamToString(v.disable_task_creation) }))}
              selectList={SELECT_LIST}
              label="作業データの非自動生成設定"
              prefix="作業データを"
              isDisplayModal={isDisplayModal}
              submitCallback={(data: { commonFlg: boolean; memberIds: number[]; type: string }) => changeDisableTaskCreation(data)}
              closeCallback={closeModal}
            />
          </>
        )
      }
    </section>
  )
}

export default Index
